import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.json';
import kh from '../locales/kh.json';
import { safeGetItem, safeSetItem } from '../utils/safeStorage';

export const LANGUAGE_STORAGE_KEY = 'storefrontLanguage';
const LEGACY_LANGUAGE_STORAGE_KEY = 'storefrontDrawerLanguage';

const translations = {
  en,
  km: kh,
};

const I18nContext = createContext({
  language: 'en',
  setLanguage: () => {},
  t: (key, fallback) => fallback || key,
});

const resolveNestedValue = (source, path) =>
  path.split('.').reduce((current, segment) => {
    if (!current || typeof current !== 'object') {
      return undefined;
    }

    return current[segment];
  }, source);

const interpolate = (template, params = {}) =>
  Object.entries(params).reduce(
    (result, [key, value]) => result.replaceAll(`{{${key}}}`, String(value)),
    template
  );

const getInitialLanguage = () => {
  const storedLanguage = safeGetItem(LANGUAGE_STORAGE_KEY)
    || safeGetItem(LEGACY_LANGUAGE_STORAGE_KEY)
    || 'en';

  return storedLanguage === 'km' ? 'km' : 'en';
};

export const I18nProvider = ({ children }) => {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    safeSetItem(LANGUAGE_STORAGE_KEY, language);
    safeSetItem(LEGACY_LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language === 'km' ? 'km' : 'en';
  }, [language]);

  const value = useMemo(() => {
    const currentTranslations = translations[language] || translations.en;

    const t = (key, paramsOrFallback, maybeFallback) => {
      const params = typeof paramsOrFallback === 'object' && paramsOrFallback !== null
        ? paramsOrFallback
        : {};
      const fallback = typeof paramsOrFallback === 'string'
        ? paramsOrFallback
        : maybeFallback;
      const resolvedValue = resolveNestedValue(currentTranslations, key)
        ?? resolveNestedValue(translations.en, key)
        ?? fallback
        ?? key;

      if (typeof resolvedValue !== 'string') {
        return resolvedValue;
      }

      return interpolate(resolvedValue, params);
    };

    return {
      language,
      setLanguage,
      t,
    };
  }, [language]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
